import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { request } from '@/api/client';
import type { DataPurpose, DataPurposeEntity } from '@/lib/dataPurpose';
import { useToast } from '@/components/Toast';
import { Badge, Button, Field, Modal, Select } from '@/components/ui';

export function PurposeBadge({ purpose }: { purpose: DataPurpose }) {
  const { t } = useTranslation();
  return <Badge tone={purpose === 'production' ? 'positive' : 'info'}>{t(`dataPurpose.${purpose}`)}</Badge>;
}

export function PurposeEditor({
  entityType, entityId, name, purpose, override, onClose, onSaved,
}: {
  entityType: DataPurposeEntity;
  entityId: string;
  name: string;
  purpose: DataPurpose;
  override?: DataPurpose | null;
  onClose: () => void;
  onSaved: (effectivePurpose: DataPurpose) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [selected, setSelected] = useState<DataPurpose | ''>(entityType === 'teams' ? override ?? '' : purpose);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const result = await request<{ data_purpose: DataPurpose }>(`/api/v1/admin/data-purpose/${entityType}/${encodeURIComponent(entityId)}`, {
        method: 'PATCH',
        body: { data_purpose: selected || null },
      });
      window.dispatchEvent(new Event('admin-data-purpose-changed'));
      toast.success(t('dataPurpose.saved'));
      onSaved(result.data_purpose);
    } catch {
      toast.failure(t('common.loadFailed'));
      setSaving(false);
    }
  }

  return <Modal open onClose={onClose} title={t('dataPurpose.editTitle', { name })}
    description={t('dataPurpose.editHint')}
    footer={<><Button onClick={onClose}>{t('common.cancel')}</Button><Button variant="primary" loading={saving} onClick={save}>{t('common.save')}</Button></>}>
    <Field label={t('dataPurpose.label')}>
      {(id) => <Select id={id} className="w-full" value={selected}
        onChange={(event) => setSelected(event.target.value as DataPurpose | '')}>
        {entityType === 'teams' ? <option value="">{t('dataPurpose.inherit')}</option> : null}
        {(['production', 'demo', 'store_review', 'internal_test'] as const).map((value) =>
          <option key={value} value={value}>{t(`dataPurpose.${value}`)}</option>)}
      </Select>}
    </Field>
    {entityType === 'teams' && override === null ? <p className="mt-2 text-xs text-ink-subtle">{t('dataPurpose.currentInherited', { value: t(`dataPurpose.${purpose}`) })}</p> : null}
  </Modal>;
}
